import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { NavigationItem } from '@/network/graphql.g'
import styled from 'styled-components'
import { HomeBanner } from '@/network/graphql.g'
import Image from 'next/image'
import { applyImagePolicy } from '@/utils/imageExtension'
import { Box } from '@/components/atoms/Grid'
import LoginButtonRedesign from '@/components/molecules/loginButtonRedesign'
import { LoginButtonTypeRedesign } from '@/components/molecules/loginButtonRedesign'
import {
  genderCategoryTestMap,
  GenderNews
} from '@/components/organisms/toolbar/desktop/campaignNavigationLinks'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import {
  lastVisitedMainCategoryKey,
  MainCategoryId
} from '../../hooks/useCurrentMainCategoryId'

type MobileSubcategoriesMenuRedesignProps = {
  categoryId: string
  navigationItems: NavigationItem[]
  onSubcategoryClick: (
    subcategory: NavigationItem,
    banners: HomeBanner[]
  ) => void
  otherMenuItems: NavigationItem[]
  currentCategoryId: string
}

export type MobileSubcategoriesMenuRedesignActions = {
  setSubcategories: (
    categoryId: string,
    subcategories: NavigationItem[]
  ) => void
}

const MobileSubcategoriesMenuRedesign = forwardRef<
  MobileSubcategoriesMenuRedesignActions,
  MobileSubcategoriesMenuRedesignProps
>((props, ref) => {
  const router = useRouter()
  const { t } = useTranslation('common')

  const [subcategoriesInfo, setSubcategoriesInfo] = useState<{
    categoryId: string
    subcategories: NavigationItem[]
  }>({
    categoryId: props.categoryId,
    subcategories: props.navigationItems
  })

  useEffect(() => {
    try {
      const lastVisited = localStorage.getItem(lastVisitedMainCategoryKey)
      if (lastVisited) {
        setSubcategoriesInfo((prev) => ({
          ...prev,
          categoryId: lastVisited
        }))
      }
      // eslint-disable-next-line no-empty
    } catch {}
  }, [])

  useImperativeHandle(ref, () => ({
    setSubcategories: (categoryId, subcategories) => {
      setSubcategoriesInfo({
        categoryId,
        subcategories
      })
    }
  }))

  const { subcategories } = subcategoriesInfo

  const handleSubcategoryClick = (
    subcategory: NavigationItem,
    banners: HomeBanner[]
  ) => {
    props.onSubcategoryClick(subcategory, banners)
  }

  const isSportAndOthers =
    subcategoriesInfo.categoryId === MainCategoryId.Sports ||
    subcategoriesInfo.categoryId === MainCategoryId.Accessories

  const getNewsUrl = (categoryId?: string | null): string | null => {
    if (!categoryId) {
      return null
    }

    if (categoryId === genderCategoryTestMap.women) {
      return GenderNews.WomenNews
    }

    if (categoryId === genderCategoryTestMap.men) {
      return GenderNews.MenNews
    }

    if (categoryId === genderCategoryTestMap.children) {
      return GenderNews.ChildrenNews
    }

    return null
  }

  const newsUrl = getNewsUrl(props.currentCategoryId)

  return (
    <>
      <SubcategoriesList ref={ref} data-testid="mobileSubcategoriesList">
        {!isSportAndOthers ? (
          <SubcategoriesGrid>
            {subcategories &&
              subcategories
                .filter(({ categoryId }) => !!categoryId)
                .map((subcategory, index) => {
                  const isLastOdd =
                    subcategories.length % 2 !== 0 &&
                    index === subcategories.length - 1
                  return (
                    <SubcategoryCard
                      key={subcategory?.categoryId}
                      $fullWidth={isLastOdd}
                      onClick={() =>
                        handleSubcategoryClick(
                          subcategory,
                          subcategory?.homeBanners
                        )
                      }
                    >
                      <SubcategoryName>{subcategory?.name}</SubcategoryName>
                      {subcategory?.url && (
                        <ImageWrapper>
                          <Image
                            src={applyImagePolicy(
                              `/static/mob-cat/mob-cat-${subcategory?.categoryId}.webp`
                            )}
                            width={34}
                            height={50}
                            unoptimized
                            objectFit="cover"
                          />
                        </ImageWrapper>
                      )}
                    </SubcategoryCard>
                  )
                })}
          </SubcategoriesGrid>
        ) : null}
        {newsUrl && (
          <SubcategoryCard onClick={() => router.push(newsUrl)}>
            <SubcategoryName> {t('Category.Filter.news')}</SubcategoryName>
          </SubcategoryCard>
        )}
        {props.otherMenuItems?.map((item) => (
          <SubcategoryCard
            key={item.id ?? item.url}
            data-testid={item.name}
            onClick={() => router.push(item.url)}
          >
            <SubcategoryName
              isHighlight={item?.isHighlight}
              isBigButton={item?.isBigButton}
            >
              {item.name}
            </SubcategoryName>
          </SubcategoryCard>
        ))}
        <Box mt={14} mb={12}>
          <LoginButtonRedesign type={LoginButtonTypeRedesign.listButton} />
        </Box>
      </SubcategoriesList>
    </>
  )
})

export default MobileSubcategoriesMenuRedesign

const SubcategoriesList = styled.ul`
  padding: 0 0 15px 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SubcategoriesGrid = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0;
  margin: 0;
  list-style: none;
`

const SubcategoryCard = styled.li<{ $fullWidth?: boolean }>`
  background: ${(props) => props.theme.colors.navMenuGrey};
  border-radius: 12px;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
  min-height: 58px;
  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    grid-column: 1 / -1;
  `}
`

const SubcategoryName = styled.h2`
  font-size: 14px;
  font-weight: ${(props) => (props.isBigButton ? 'bold' : '500')};
  color: ${(props) =>
    props.isHighlight
      ? props.theme.colors.navMenuRed
      : props.theme.colors.onBackground};
  margin: 0;
  text-transform: ${(props) => props.isBigButton && 'uppercase'};
`

const ImageWrapper = styled.div`
  width: 60px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`
